const FORBIDDEN_BLOCK_TAGS = /<\s*\/?\s*(script|style|iframe|object|embed|form|input|button|textarea|select|option|meta|link|base)\b[^>]*>/gi;
const ALLOWED_HTML_TAGS = new Set([
  'p',
  'br',
  'strong',
  'em',
  'b',
  'i',
  'u',
  's',
  'ul',
  'ol',
  'li',
  'blockquote',
  'code',
  'pre',
  'a',
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
]);

const SELF_CLOSING_TAGS = new Set(['br']);

const escapeHtml = (value: string): string =>
  value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

const isSafeHref = (href: string) => /^(https?:|mailto:|tel:|\/|#)/i.test(href.trim());

const linkifyPlainText = (text: string): string => {
  const urlRegex = /(https?:\/\/[a-zA-Z0-9-._~:/?#[\]@!$&'()*+,;=%]+)/g;
  return text.replace(urlRegex, (url) => {
    const safeUrl = escapeHtml(url);
    return `<a href="${safeUrl}" target="_blank" rel="noopener noreferrer" class="text-blue-500 hover:underline">${safeUrl}</a>`;
  });
};

const sanitizeAllowedHtml = (content: string): string => {
  const withoutDangerousBlocks = content
    .replace(/<!--[\s\S]*?-->/g, '')
    .replace(/<\s*(script|style|iframe|object|embed|form|input|button|textarea|select|option|meta|link|base)\b[\s\S]*?<\s*\/\s*\1\s*>/gi, '')
    .replace(FORBIDDEN_BLOCK_TAGS, '');

  return withoutDangerousBlocks.replace(/<\/?[^>]+>/g, (tag) => {
    const match = tag.match(/^<\s*(\/)?\s*([a-z0-9-]+)/i);
    if (!match) {
      return '';
    }

    const [, closingSlash, rawTagName] = match;
    const tagName = rawTagName.toLowerCase();

    if (!ALLOWED_HTML_TAGS.has(tagName)) {
      return '';
    }

    if (closingSlash) {
      return `</${tagName}>`;
    }

    if (tagName === 'a') {
      const hrefMatch = tag.match(/href\s*=\s*(?:"([^"]*)"|'([^']*)'|([^\s>]+))/i);
      const href = hrefMatch?.[1] || hrefMatch?.[2] || hrefMatch?.[3] || '';

      if (!href || !isSafeHref(href)) {
        return '<a>';
      }

      return `<a href="${escapeHtml(href)}" target="_blank" rel="noopener noreferrer">`;
    }

    if (SELF_CLOSING_TAGS.has(tagName)) {
      return `<${tagName} />`;
    }

    return `<${tagName}>`;
  });
};

export const formatContentForDisplay = (content: string): string => {
  if (!content) return '';

  let processedContent = linkifyPlainText(content);
  processedContent = processedContent.replace(/\n/g, '<br />');

  return processedContent;
};

export const formatForumPostContent = (content: string): string => {
  if (!content) return '';

  const hasHtmlTags = /<\/?[a-z][\s\S]*?>/i.test(content);

  if (!hasHtmlTags) {
    return linkifyPlainText(escapeHtml(content)).replace(/\n/g, '<br />');
  }

  return sanitizeAllowedHtml(content);
};
